/**
 * /admin/users — paginated table of every user in the tenant.
 *
 * Per-row Deactivate / Reactivate prompts for an audit reason via the
 * `promptForReason` modal (no more `window.prompt`). Detail (role +
 * grant management) lives at /admin/users/:id.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  IconUserCheck,
  IconUserX,
} from "@tabler/icons-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  deactivateUser,
  listUsers,
  reactivateUser,
  type UserOut,
} from "@/shared/api/users";

const PAGE_SIZE = 25;

export function UsersList() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [items, setItems] = useState<UserOut[]>([]);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (cursor: string | null, filter: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const page = await listUsers(token, {
          limit: PAGE_SIZE,
          cursor,
          status: filter,
        });
        setItems(page.items);
        setNextCursor(page.next_cursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [getToken],
  );

  useEffect(() => {
    void load(null, statusFilter);
    setCursorHistory([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const onNext = () => {
    if (!nextCursor) return;
    setCursorHistory((h) => [...h, nextCursor]);
    void load(nextCursor, statusFilter);
  };

  const onPrev = () => {
    const target =
      cursorHistory.length >= 2 ? cursorHistory[cursorHistory.length - 2] : null;
    setCursorHistory((h) => h.slice(0, -2));
    void load(target ?? null, statusFilter);
  };

  const onToggleStatus = async (user: UserOut) => {
    const verb = user.status === "deactivated" ? "Reactivate" : "Deactivate";
    const reason = await promptForReason(
      `${verb} ${user.display_name || user.user_id}`,
      "Audit reason",
    );
    if (!reason) return;
    try {
      const token = await getToken();
      if (user.status === "deactivated") {
        await reactivateUser(token, user.user_id, reason);
        toastSuccess(
          "Reactivated",
          `${user.display_name || user.user_id} can sign in again.`,
        );
      } else {
        await deactivateUser(token, user.user_id, reason);
        toastWarning(
          "Deactivated",
          `${user.display_name || user.user_id} can no longer sign in.`,
        );
      }
      const lastCursor = cursorHistory[cursorHistory.length - 1] ?? null;
      void load(lastCursor, statusFilter);
    } catch (err) {
      toastApiError("Action failed", err);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Users</Title>
        <Button
          component={RouterLink}
          to="/admin/users/new"
          leftSection={<IconPlus size={16} />}
        >
          Provision user
        </Button>
      </Group>

      <Group>
        <Select
          label="Status"
          placeholder="All"
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            { value: "pending", label: "Pending" },
            { value: "active", label: "Active" },
            { value: "deactivated", label: "Deactivated" },
          ]}
          clearable
          w={200}
        />
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not load users">
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p={0}>
        {loading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : items.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            No users match.
          </Text>
        ) : (
          <Table verticalSpacing="sm" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Display name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Roles</Table.Th>
                <Table.Th>Last login (UTC)</Table.Th>
                <Table.Th style={{ width: 60 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((u) => (
                <Table.Tr
                  key={u.user_id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/admin/users/${u.user_id}`)}
                >
                  <Table.Td>
                    {u.display_name || <Text c="dimmed">(hidden)</Text>}
                  </Table.Td>
                  <Table.Td>{u.email || <Text c="dimmed">—</Text>}</Table.Td>
                  <Table.Td>
                    <Badge color={statusColor(u.status)} variant="light">
                      {u.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {u.roles.length === 0 ? (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      ) : (
                        u.roles.map((r) => (
                          <Badge key={r} color="brand" variant="light" size="sm">
                            {r}
                          </Badge>
                        ))
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {u.last_login_at_utc ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                      label={u.status === "deactivated" ? "Reactivate" : "Deactivate"}
                    >
                      <ActionIcon
                        variant="subtle"
                        color={u.status === "deactivated" ? "green" : "red"}
                        onClick={() => void onToggleStatus(u)}
                        aria-label={
                          u.status === "deactivated" ? "Reactivate" : "Deactivate"
                        }
                      >
                        {u.status === "deactivated" ? (
                          <IconUserCheck size={18} />
                        ) : (
                          <IconUserX size={18} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Group justify="flex-end" gap="xs">
        <Button
          variant="default"
          onClick={onPrev}
          disabled={cursorHistory.length === 0}
          leftSection={<IconChevronLeft size={16} />}
        >
          Previous
        </Button>
        <Button
          variant="default"
          onClick={onNext}
          disabled={!nextCursor}
          rightSection={<IconChevronRight size={16} />}
        >
          Next
        </Button>
      </Group>
    </Stack>
  );
}

function statusColor(status: string): string {
  switch (status) {
    case "active":
      return "green";
    case "pending":
      return "yellow";
    case "deactivated":
      return "red";
    default:
      return "gray";
  }
}
